import React from 'react';
import { ScrollView, View, Alert, Linking, Image } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import { useTranslation } from 'react-i18next';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { Text, Card, ListRow, Switch, Button, AppBar } from '@ui';
import { usePreferencesStore } from '@stores/preferencesStore';
import { useAdvisor } from '@api/hooks';

const Container = styled(SafeAreaView)`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Content = styled(ScrollView)`
  flex: 1;
`;

const Section = styled.View`
  padding: ${({ theme }) => theme.spacing.base}px;
  margin-bottom: ${({ theme }) => theme.spacing.sm}px;
`;

const ProfileCard = styled(Card)`
  flex-direction: row;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const Avatar = styled(Image)`
  width: 72px;
  height: 72px;
  border-radius: 36px;
  background-color: ${({ theme }) => theme.colors.neutral200};
  margin-right: ${({ theme }) => theme.spacing.base}px;
`;

const ProfileInfo = styled.View`
  flex: 1;
`;

const SectionTitle = styled(Text)`
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
`;

const SettingsCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.sm}px;
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const ProfileScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    language,
    notificationsEnabled,
    dataSharing,
    setLanguage,
    setNotificationsEnabled,
    updateDataSharing,
  } = usePreferencesStore();
  const { data: advisor } = useAdvisor();

  const handleLanguageChange = () => {
    const newLang = language === 'en' ? 'zh-HK' : 'en';
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
  };

  const handleExportData = () => {
    Alert.alert(
      'Export Data',
      'Your data export will be prepared and sent to your email within 24 hours.',
      [{ text: 'OK' }]
    );
  };

  const handleDeleteAccount = () => {
    Alert.alert(
      'Delete Account',
      'Are you sure you want to delete your account? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            Alert.alert('Account Deletion', 'This is a demo. No action taken.');
          },
        },
      ]
    );
  };

  const handleSignOut = () => {
    Alert.alert('Sign Out', 'Are you sure you want to sign out?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Sign Out',
        onPress: () => {
          Alert.alert('Signed Out', 'This is a demo. You remain signed in.');
        },
      },
    ]);
  };

  return (
    <Container edges={['top']}>
      <AppBar title={t('profile.title')} />
      <Content showsVerticalScrollIndicator={false}>
        {/* Profile Section */}
        <Section>
          <ProfileCard padding="md">
            <Avatar
              source={{
                uri: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80',
              }}
              resizeMode="cover"
            />
            <ProfileInfo>
              <Text variant="title" color="textPrimary">
                Demo User
              </Text>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginTop: 2 }}>
                demo@aia-member.com
              </Text>
              <Text variant="bodySmall" color="textSecondary" style={{ marginTop: 4 }}>
                Member since Nov 2025
              </Text>
            </ProfileInfo>
          </ProfileCard>
        </Section>

        {/* Settings */}
        <Section>
          <SectionTitle variant="title" color="textPrimary">
            {t('profile.settings')}
          </SectionTitle>
          <SettingsCard>
            <ListRow
              title={t('profile.notifications')}
              subtitle="Manage push notifications"
              leftIcon="notifications"
              rightElement={
                <Switch value={notificationsEnabled} onValueChange={setNotificationsEnabled} />
              }
              divider
              onPress={() => {}}
            />
            <ListRow
              title={t('profile.language')}
              subtitle={language === 'en' ? 'English' : '繁體中文'}
              leftIcon="language"
              rightIcon="chevron-right"
              onPress={handleLanguageChange}
            />
          </SettingsCard>
        </Section>

        {/* Privacy & Data */}
        <Section>
          <SectionTitle variant="title" color="textPrimary">
            {t('profile.privacy')}
          </SectionTitle>
          <SettingsCard>
            <Switch
              label="Analytics & Performance"
              description="Help us improve the app"
              value={dataSharing.analytics}
              onValueChange={v => updateDataSharing('analytics', v)}
            />
            <Switch
              label="Marketing Communications"
              description="Receive personalized offers"
              value={dataSharing.marketing}
              onValueChange={v => updateDataSharing('marketing', v)}
            />
            <Switch
              label="Research & Development"
              description="Contribute to product research"
              value={dataSharing.research}
              onValueChange={v => updateDataSharing('research', v)}
            />
          </SettingsCard>
        </Section>

        {/* Support & Legal */}
        <Section>
          <SectionTitle variant="title" color="textPrimary">
            {t('profile.help')}
          </SectionTitle>
          <SettingsCard>
            <ListRow
              title="Help Center"
              leftIcon="help"
              rightIcon="chevron-right"
              onPress={() => Linking.openURL('https://help.aia.com.hk')}
            />
            <ListRow
              title="Terms of Service"
              leftIcon="description"
              rightIcon="chevron-right"
              divider
              onPress={() => Linking.openURL('https://aia.com.hk/terms')}
            />
            <ListRow
              title="Privacy Policy"
              leftIcon="privacy-tip"
              rightIcon="chevron-right"
              onPress={() => Linking.openURL('https://aia.com.hk/privacy')}
            />
          </SettingsCard>
        </Section>

        {/* Account Actions */}
        <Section>
          <Button
            variant="tertiary"
            fullWidth
            iconLeft="download"
            onPress={handleExportData}
            style={{ marginBottom: 12 }}
          >
            {t('profile.exportData')}
          </Button>
          <Button
            variant="ghost"
            fullWidth
            iconLeft="delete"
            onPress={handleDeleteAccount}
            style={{ marginBottom: 12 }}
          >
            {t('profile.deleteAccount')}
          </Button>
          <Button variant="tertiary" fullWidth iconLeft="logout" onPress={handleSignOut}>
            {t('profile.signOut')}
          </Button>
        </Section>

        <View style={{ height: 24 }} />
      </Content>
    </Container>
  );
};

export default ProfileScreen;
